import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import DashboardHeader from '../../components/common/DashboardHeader';
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface AdminUser {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  role: 'admin' | 'super_admin';
  status: 'active' | 'inactive' | 'suspended';
  privileges: AdminPrivileges;
  created_at: string;
  last_login: string;
  created_by: string;
}

interface AdminPrivileges {
  manage_garages: boolean;
  manage_users: boolean;
  manage_subscriptions: boolean;
  view_reports: boolean;
  manage_payments: boolean;
  send_notifications: boolean;
  view_analytics: boolean;
  export_data: boolean;
  manage_settings: boolean;
  access_logs: boolean;
  approve_garages: boolean;
  financial_reports: boolean;
}

const AdminPrivilegesPage: React.FC = () => {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    phone_number: '',
    privileges: {
      manage_garages: false,
      manage_users: false,
      manage_subscriptions: false,
      view_reports: false,
      manage_payments: false,
      send_notifications: false,
      view_analytics: false,
      export_data: false,
      manage_settings: false,
      access_logs: false,
      approve_garages: false,
      financial_reports: false,
    },
  });

  useEffect(() => {
    fetchAdmins();
  }, [search]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockAdmins: AdminUser[] = [
        {
          id: 1,
          name: 'Ahmed Al-Rashid',
          email: 'ahmed.rashid@rayyi7.com',
          phone_number: '+965 9876 5432',
          role: 'admin',
          status: 'active',
          privileges: {
            manage_garages: true,
            manage_users: true,
            manage_subscriptions: true,
            view_reports: true,
            manage_payments: false,
            send_notifications: true,
            view_analytics: true,
            export_data: false,
            manage_settings: false,
            access_logs: false,
            approve_garages: true,
            financial_reports: false,
          },
          created_at: '2024-01-10T10:00:00Z',
          last_login: '2024-01-15T09:30:00Z',
          created_by: 'Super Admin',
        },
        {
          id: 2,
          name: 'Sarah Al-Mutairi',
          email: 'sarah.mutairi@rayyi7.com',
          phone_number: '+965 8765 4321',
          role: 'admin',
          status: 'active',
          privileges: {
            manage_garages: true,
            manage_users: false,
            manage_subscriptions: true,
            view_reports: true,
            manage_payments: false,
            send_notifications: true,
            view_analytics: false,
            export_data: false,
            manage_settings: false,
            access_logs: false,
            approve_garages: false,
            financial_reports: false,
          },
          created_at: '2024-01-12T14:30:00Z',
          last_login: '2024-01-15T08:15:00Z',
          created_by: 'Super Admin',
        },
        {
          id: 3,
          name: 'Mohammad Al-Sabah',
          email: 'mohammad.sabah@rayyi7.com',
          phone_number: '+965 7654 3210',
          role: 'admin',
          status: 'inactive',
          privileges: {
            manage_garages: false,
            manage_users: false,
            manage_subscriptions: false,
            view_reports: true,
            manage_payments: false,
            send_notifications: false,
            view_analytics: true,
            export_data: false,
            manage_settings: false,
            access_logs: false,
            approve_garages: false,
            financial_reports: false,
          },
          created_at: '2024-01-08T16:45:00Z',
          last_login: '2024-01-13T17:20:00Z',
          created_by: 'Super Admin',
        },
      ];

      await new Promise(resolve => setTimeout(resolve, 500));
      setAdmins(mockAdmins.filter(admin => 
        admin.name.toLowerCase().includes(search.toLowerCase()) ||
        admin.email.toLowerCase().includes(search.toLowerCase())
      ));
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Failed to load admin users');
    } finally {
      setLoading(false);
    }
  };

  const createAdmin = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Admin user created successfully');
      setShowCreateModal(false);
      setNewAdmin({
        name: '',
        email: '',
        phone_number: '',
        privileges: {
          manage_garages: false,
          manage_users: false,
          manage_subscriptions: false,
          view_reports: false,
          manage_payments: false,
          send_notifications: false,
          view_analytics: false,
          export_data: false,
          manage_settings: false,
          access_logs: false,
          approve_garages: false,
          financial_reports: false,
        },
      });
      fetchAdmins();
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error('Failed to create admin user');
    }
  };

  const updateAdminPrivileges = async (adminId: number, privileges: AdminPrivileges) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Admin privileges updated successfully');
      setShowEditModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin privileges:', error);
      toast.error('Failed to update admin privileges');
    }
  };

  const toggleAdminStatus = async (adminId: number, newStatus: string) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success(`Admin ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast.error('Failed to update admin status');
    }
  };

  const deleteAdmin = async (adminId: number) => {
    if (!window.confirm('Are you sure you want to delete this admin user? This action cannot be undone.')) {
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('Admin user deleted successfully');
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('Failed to delete admin user');
    }
  };

  const privilegeLabels = {
    manage_garages: 'Manage Garages',
    manage_users: 'Manage Users',
    manage_subscriptions: 'Manage Subscriptions',
    view_reports: 'View Reports',
    manage_payments: 'Manage Payments',
    send_notifications: 'Send Notifications',
    view_analytics: 'View Analytics',
    export_data: 'Export Data',
    manage_settings: 'Manage Settings',
    access_logs: 'Access Logs',
    approve_garages: 'Approve Garages',
    financial_reports: 'Financial Reports',
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <ShieldCheckIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
                Admin Privileges Management
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage admin users and their privileges</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-[#008B8B] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-[#006666] flex items-center justify-center text-sm sm:text-base"
            >
              <PlusIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-2" />
              <span className="hidden sm:inline">Create Admin</span>
              <span className="sm:hidden">Add Admin</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <UserGroupIcon className="w-6 sm:w-8 h-6 sm:h-8 text-[#008B8B] mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Total Admins</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{admins.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-6 sm:w-8 h-6 sm:h-8 text-green-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Active</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {admins.filter(a => a.status === 'active').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="w-6 sm:w-8 h-6 sm:h-8 text-yellow-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Inactive</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {admins.filter(a => a.status === 'inactive').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center">
              <KeyIcon className="w-6 sm:w-8 h-6 sm:h-8 text-purple-600 mr-2 sm:mr-3" />
              <div>
                <p className="text-xs sm:text-sm text-gray-600">Avg Privileges</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">
                  {admins.length > 0 ? Math.round(
                    admins.reduce((sum, admin) => 
                      sum + Object.values(admin.privileges).filter(Boolean).length, 0
                    ) / admins.length
                  ) : 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 sm:w-5 h-4 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search admin users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 sm:pl-10 w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
            />
          </div>
        </div>

        {/* Admins Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Privileges
                  </th>
                  <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#008B8B] mx-auto"></div>
                    </td>
                  </tr>
                ) : admins.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No admin users found
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 sm:h-10 w-8 sm:w-10 bg-[#008B8B] rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {admin.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                          <div className="ml-2 sm:ml-4">
                            <div className="text-xs sm:text-sm font-medium text-gray-900">{admin.name}</div>
                            <div className="text-xs sm:text-sm text-gray-500">ID: {admin.id}</div>
                            <div className="sm:hidden text-xs text-gray-500 mt-1">{admin.email}</div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900">{admin.email}</div>
                          <div className="text-sm text-gray-500">{admin.phone_number}</div>
                        </div>
                      </td>
                      
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <span className={`px-1 sm:px-2 py-0.5 sm:py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(admin.status)}`}>
                          {admin.status}
                        </span>
                      </td>
                      
                      <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {Object.values(admin.privileges).filter(Boolean).length} of {Object.keys(admin.privileges).length}
                        </div>
                        <div className="text-xs text-gray-500">privileges enabled</div>
                      </td>
                      
                      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {admin.last_login ? new Date(admin.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      
                      <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-1 sm:space-x-2">
                          <button
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Privileges"
                          >
                            <PencilIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                          
                          <button
                            onClick={() => toggleAdminStatus(admin.id, admin.status === 'active' ? 'inactive' : 'active')}
                            className={admin.status === 'active' ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}
                            title={admin.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {admin.status === 'active' ? (
                              <ExclamationTriangleIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            ) : (
                              <CheckCircleIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                            )}
                          </button>
                          
                          <button
                            onClick={() => deleteAdmin(admin.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Admin Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Create New Admin</h2>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                      <input
                        type="text"
                        value={newAdmin.name}
                        onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={newAdmin.email}
                        onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input
                        type="tel"
                        value={newAdmin.phone_number}
                        onChange={(e) => setNewAdmin({ ...newAdmin, phone_number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008B8B] focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Privileges</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {Object.entries(privilegeLabels).map(([key, label]) => (
                        <label key={key} className="flex items-center p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={newAdmin.privileges[key as keyof AdminPrivileges]}
                            onChange={(e) => setNewAdmin({
                              ...newAdmin,
                              privileges: {
                                ...newAdmin.privileges,
                                [key]: e.target.checked,
                              },
                            })}
                            className="mr-3 rounded border-gray-300 text-[#008B8B] focus:ring-[#008B8B]"
                          />
                          <span className="text-sm text-gray-700">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createAdmin}
                    className="px-6 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]"
                  >
                    Create Admin
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Privileges Modal */}
        {showEditModal && selectedAdmin && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Edit Privileges - {selectedAdmin.name}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {Object.entries(privilegeLabels).map(([key, label]) => (
                    <label key={key} className="flex items-center p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={selectedAdmin.privileges[key as keyof AdminPrivileges]}
                        onChange={(e) => setSelectedAdmin({
                          ...selectedAdmin,
                          privileges: {
                            ...selectedAdmin.privileges,
                            [key]: e.target.checked,
                          },
                        })}
                        className="mr-3 rounded border-gray-300 text-[#008B8B] focus:ring-[#008B8B]"
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>

                <div className="flex justify-end space-x-3 pt-6">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedAdmin(null);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => updateAdminPrivileges(selectedAdmin.id, selectedAdmin.privileges)}
                    className="px-6 py-2 bg-[#008B8B] text-white rounded-lg hover:bg-[#006666]"
                  >
                    Update Privileges
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPrivilegesPage;